import styles from './ProgressRing.module.css';

export interface ProgressRingProps {
  /** 0–100; clamped and rounded to a whole number for both the arc and the centered label. */
  percent: number;
  /** The arc (progress) color — pass a status tint; the track stays neutral grey. */
  arcColor: string;
  /** Outer diameter in px (default 72). */
  size?: number;
  /** Ring thickness in px (default 10). */
  stroke?: number;
}

// A circular completion ring — a grey track under a status-colored arc that fills clockwise from 12
// o'clock, with the whole-number percent centered inside. Mirrors the native apps' CompletionRing. The
// arc uses the standard strokeDasharray = circumference / strokeDashoffset technique; the svg is rotated
// -90deg so 0% starts at the top, and the label sits in a non-rotated overlay so it stays upright.
export function ProgressRing({ percent, arcColor, size = 72, stroke = 10 }: ProgressRingProps) {
  const clamped = Math.max(0, Math.min(100, Math.round(percent)));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - clamped / 100);
  const center = size / 2;
  return (
    <div className={styles.ring} style={{ width: size, height: size }}>
      <svg className={styles.svg} width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
        <circle className={styles.track} cx={center} cy={center} r={radius} fill="none" strokeWidth={stroke} />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          style={{ stroke: arcColor }}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <span className={styles.value}>
        {/* One inline run, so the smaller "%" sits on the number's baseline — the Figma ring label, and
            native's `CompletionRing` (an HStack aligned on `.lastTextBaseline`). As sibling flex items the
            two would centre-align instead and the "%" would float. */}
        <span className={styles.number} style={{ fontSize: Math.round(size * 0.25) }}>
          {clamped}
          <span className={styles.pct}>%</span>
        </span>
      </span>
    </div>
  );
}
